/**
 * Vista de lista: lo mismo que el tablero, en una tabla. Útil para ordenar de un vistazo y en celulares
 * (la tabla se desplaza en horizontal dentro de su contenedor, no la página).
 *
 * La columna Estado muestra el nombre del estado o, si está cerrada, su marca («Hecha» / «Descartada»).
 * La columna «Fecha límite» (CDT-76) marca las atrasadas y las que vencen pronto; su encabezado es un botón que
 * ordena por fecha límite (la más próxima primero, sin fecha al final) o vuelve al orden de la API.
 *
 * La fila entera es clicable con el mouse; para el teclado, la celda de la tarea trae un botón real
 * (el clic del botón sube hasta la fila, así que solo se maneja ahí y no se abre dos veces).
 */
import { useState } from 'react';
import {
  ETIQUETA_ESFUERZO,
  esEstadoCerrado,
  formatearNumero,
  hoyEnChile,
  type Tarea,
} from '@cdtec-backlog/contrato';
import { Avatar, nombreCorto } from '../../componentes/Avatar';
import { Esqueleto, TextoCargando, type AnchoEsqueleto } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { fechaCorta } from '../../lib/formato';
import { EtiquetaPrioridad } from './EtiquetasTarea';
import { MarcaFechaLimite } from './FechaLimite';
import { IconoTipo } from './IconoTipo';
import { EstadoTarea } from './MarcaCierre';
import './ListaTareas.css';

/**
 * Orden por fecha límite: la más próxima (o la más atrasada) primero y las sin fecha al final. Es estable: entre
 * iguales se mantiene el orden de la API (prioridad y última actividad).
 */
export function ordenarPorFechaLimite(tareas: readonly Tarea[]): Tarea[] {
  return [...tareas].sort((a, b) => {
    if (a.fechaLimite === b.fechaLimite) return 0;
    if (a.fechaLimite === null) return 1;
    if (b.fechaLimite === null) return -1;
    return a.fechaLimite < b.fechaLimite ? -1 : 1;
  });
}

/**
 * Encabezado de la tabla: es el mismo en la carga (se ve real desde el principio). Con `onOrdenar`, «Fecha límite»
 * es un botón que alterna el orden (y `aria-sort` lo anuncia).
 */
function Encabezado({
  porFecha = false,
  onOrdenar,
}: {
  porFecha?: boolean;
  onOrdenar?: () => void;
}) {
  return (
    <thead>
      <tr>
        <th>Tarea</th>
        <th>Estado</th>
        <th>Prioridad</th>
        <th>Proyecto</th>
        <th>Responsable</th>
        <th>Esfuerzo</th>
        <th aria-sort={onOrdenar ? (porFecha ? 'ascending' : 'none') : undefined}>
          {onOrdenar ? (
            <button
              type="button"
              className={`lista__ordenar${porFecha ? ' activo' : ''}`}
              title={porFecha ? 'Volver al orden por prioridad' : 'Ordenar por fecha límite'}
              onClick={onOrdenar}
            >
              Fecha límite
              <span aria-hidden="true">{porFecha ? ' ↑' : ''}</span>
            </button>
          ) : (
            'Fecha límite'
          )}
        </th>
        <th>Actualizada</th>
      </tr>
    </thead>
  );
}

const ANCHOS_TITULO: AnchoEsqueleto[] = [70, 50, 60, 80, 40];

/**
 * Primera carga de la lista: encabezado real y 5 filas fantasma con las mismas columnas y alto de fila.
 * `mostrar` llega a los ~300 ms; antes las filas ocupan su lugar invisibles.
 */
export function ListaTareasCargando({ mostrar }: { mostrar: boolean }) {
  return (
    <>
      <TextoCargando>Cargando tareas…</TextoCargando>
      <div className={`tabla-contenedor${mostrar ? '' : ' carga--espera'}`} aria-busy="true">
        <table className="tabla tabla--lista">
          <Encabezado />
          <tbody>
            {ANCHOS_TITULO.map((ancho) => (
              <tr key={ancho} className="lista__fila--fantasma" aria-hidden="true">
                <td>
                  <span className="lista__fantasma">
                    <Esqueleto forma="icono" />
                    <Esqueleto className="lista__fantasma-id" />
                    <Esqueleto ancho={ancho} />
                  </span>
                </td>
                <td>
                  <Esqueleto forma="chip" />
                </td>
                <td>
                  <Esqueleto forma="chip" />
                </td>
                <td>
                  <Esqueleto forma="chip" />
                </td>
                <td>
                  <span className="lista__persona">
                    <Esqueleto forma="avatar" />
                    <Esqueleto className="lista__fantasma-nombre" />
                  </span>
                </td>
                <td>
                  <Esqueleto className="lista__fantasma-corto" />
                </td>
                <td>
                  <Esqueleto className="lista__fantasma-corto" />
                </td>
                <td>
                  <Esqueleto className="lista__fantasma-corto" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ListaTareas({
  tareas,
  onAbrir,
  hoy = hoyEnChile(),
}: {
  tareas: Tarea[];
  onAbrir: (id: number) => void;
  /** Día de hoy en Chile (`AAAA-MM-DD`). Por defecto, ahora (las pruebas lo fijan). */
  hoy?: string;
}) {
  const [porFecha, setPorFecha] = useState(false);
  const filas = porFecha ? ordenarPorFechaLimite(tareas) : tareas;
  return (
    <div className="tabla-contenedor">
      <table className="tabla tabla--lista">
        <Encabezado porFecha={porFecha} onOrdenar={() => setPorFecha((actual) => !actual)} />
        <tbody>
          {filas.map((tarea) => (
            <tr
              key={tarea.id}
              className={`lista__fila${esEstadoCerrado(tarea.estado) ? ' lista__fila--cerrada' : ''}${tarea.estado === 'DESCARTADA' ? ' lista__fila--descartada' : ''}`}
              onClick={() => onAbrir(tarea.id)}
            >
              <td>
                <button type="button" className="lista__abrir">
                  <IconoTipo tipo={tarea.tipo} />
                  <span className="lista__id">{formatearNumero(tarea.numero)}</span>
                  <span className="lista__titulo">{tarea.titulo}</span>
                  {tarea.adjuntos > 0 && (
                    <span className="lista__clip" title={`${tarea.adjuntos} adjuntos`}>
                      <Icono nombre="clip" tamano={12} />
                      {tarea.adjuntos}
                    </span>
                  )}
                </button>
              </td>
              <td>
                <EstadoTarea estado={tarea.estado} />
              </td>
              <td>
                <EtiquetaPrioridad prioridad={tarea.prioridad} />
              </td>
              <td>
                {tarea.proyecto ? (
                  <span className="etiqueta etiqueta--proyecto" title={tarea.proyecto.nombre}>
                    {tarea.proyecto.nombre}
                  </span>
                ) : (
                  <span className="lista__vacio">—</span>
                )}
              </td>
              <td>
                <span className="lista__persona" title={tarea.responsable?.nombre}>
                  {tarea.responsable && <Avatar nombre={tarea.responsable.nombre} />}
                  {tarea.responsable ? nombreCorto(tarea.responsable.nombre) : 'Sin asignar'}
                </span>
              </td>
              <td>{ETIQUETA_ESFUERZO[tarea.esfuerzo]}</td>
              <td className="lista__limite">
                {tarea.fechaLimite ? (
                  <MarcaFechaLimite tarea={tarea} hoy={hoy} />
                ) : (
                  <span className="lista__vacio">—</span>
                )}
              </td>
              <td className="lista__fecha">{fechaCorta(tarea.actualizadoEn)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
